import { clearElement, createElement, requireElement } from "../ui/dom";
import {
  TCT_CATEGORY_LABELS,
  TCT_SCENARIOS,
  findTctScenario,
} from "./scenarios";
import { simulateTctScenario } from "./simulator";
import { TCT_APP_TEMPLATE } from "./template";
import type {
  TctScenario,
  TctSimulationResult,
  TctStage,
  TctStageState,
  TctTimelineStep,
} from "./types";

const DEFAULT_TCT_SCENARIO = "catalog-discovery";
const STAGES: readonly TctStage[] = ["entry", "catalog", "murl", "curl"];

const STAGE_LABELS: Readonly<Record<TctStageState, string>> = {
  idle: "Not reached",
  observed: "Observed",
  candidate: "Candidate",
  confirmed: "Confirmed",
  reused: "Cached reuse",
  blocked: "Blocked",
  rejected: "Rejected",
};

function setText(selector: string, value: string): void {
  requireElement<HTMLElement>(selector).textContent = value;
}

function renderDraftLinks(scenario: TctScenario): void {
  const container = requireElement<HTMLElement>("#tct-draft-links");
  clearElement(container);
  for (const reference of scenario.references) {
    const link = createElement("a", "scenario-draft-link", reference.label);
    link.href = reference.href;
    link.target = "_blank";
    link.rel = "noreferrer";
    container.append(link);
  }
}

function renderTimelineStep(step: TctTimelineStep): HTMLElement {
  const article = createElement("article", "timeline-step");
  article.dataset.tone = step.tone;

  const number = createElement("span", "timeline-step__number", step.number);
  number.setAttribute("aria-hidden", "true");
  const copy = createElement("div", "timeline-step__copy");
  copy.append(
    createElement("p", "eyebrow", step.eyebrow),
    createElement("h4", undefined, step.title),
    createElement("p", "timeline-step__detail", step.detail),
  );
  const wire = createElement("pre", "timeline-step__wire");
  wire.textContent = step.wire;
  article.append(number, copy, wire);
  return article;
}

function renderResult(result: TctSimulationResult): void {
  for (const stage of STAGES) {
    const state = result.stages[stage];
    const node = requireElement<HTMLElement>(`#tct-stage-${stage}`);
    node.dataset.state = state;
    setText(`#tct-stage-${stage}-state`, STAGE_LABELS[state]);
  }

  const outcome = requireElement<HTMLElement>("#tct-outcome");
  outcome.dataset.outcome = result.outcome;
  setText("#tct-layer-label", result.layerLabel);
  setText("#tct-outcome-label", result.outcomeLabel);
  setText("#tct-outcome-explanation", result.explanation);
  setText(
    "#tct-outcome-count",
    `${result.requestCount} displayed HTTP request${result.requestCount === 1 ? "" : "s"} · fixed local fixture`,
  );

  const timeline = requireElement<HTMLElement>("#tct-timeline");
  clearElement(timeline);
  for (const step of result.steps) {
    timeline.append(renderTimelineStep(step));
  }
}

function renderScenario(scenario: TctScenario): void {
  setText("#tct-scenario-category", TCT_CATEGORY_LABELS[scenario.category]);
  setText("#tct-scenario-title", scenario.title);
  setText("#tct-scenario-summary", scenario.summary);
  setText("#tct-scenario-rule", scenario.rule);
  renderDraftLinks(scenario);
  renderResult(simulateTctScenario(scenario));
}

function populateScenarioSelect(select: HTMLSelectElement): void {
  clearElement(select);
  for (const category of Object.keys(TCT_CATEGORY_LABELS) as Array<
    keyof typeof TCT_CATEGORY_LABELS
  >) {
    const group = document.createElement("optgroup");
    group.label = TCT_CATEGORY_LABELS[category];
    for (const scenario of TCT_SCENARIOS.filter(
      (candidate) => candidate.category === category,
    )) {
      const option = document.createElement("option");
      option.value = scenario.id;
      option.textContent = scenario.title;
      group.append(option);
    }
    select.append(group);
  }
}

export function mountTctDemo(): void {
  const app = requireElement<HTMLDivElement>("#app");
  app.innerHTML = TCT_APP_TEMPLATE;

  const select = requireElement<HTMLSelectElement>("#tct-scenario-select");
  populateScenarioSelect(select);
  setText(
    "#tct-scenario-count",
    `${TCT_SCENARIOS.length} deterministic local scenarios · selection evaluates immediately`,
  );
  setText(
    "#tct-evidence-count",
    `${TCT_SCENARIOS.length} deterministic local cases`,
  );

  const initial = findTctScenario(DEFAULT_TCT_SCENARIO);
  if (initial === undefined) {
    throw new Error(`Default TCT scenario is missing: ${DEFAULT_TCT_SCENARIO}`);
  }
  select.value = initial.id;
  renderScenario(initial);

  select.addEventListener("change", () => {
    const scenario = findTctScenario(select.value);
    if (scenario !== undefined) {
      renderScenario(scenario);
    }
  });
}
